import React, { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import styles from "./BrokerCRM.module.css"
import logoPng from "../../assets/mm-logo.png"
import rosPhoto from "../../assets/Ros.png"

const STORAGE_KEY = "mmBrokerProfile"
const SESSION_EXPIRY_KEY = "mmBrokerProfileExpiry"

const PROFILE_PHOTOS = {
  leah_associate: rosPhoto,
}

function getInitials(name = "") {
  return String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("")
}

export default function CRMTopNav() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [loggedInUser, setLoggedInUser] = useState(null)

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 960) setOpen(false)
    }

    const handleOutsideClick = (e) => {
      if (!e.target.closest(`.${styles.crmUserMenuWrap}`)) {
        setProfileOpen(false)
      }
    }

    window.addEventListener("resize", onResize)
    document.addEventListener("click", handleOutsideClick)

    return () => {
      window.removeEventListener("resize", onResize)
      document.removeEventListener("click", handleOutsideClick)
    }
  }, [styles.crmUserMenuWrap])

  useEffect(() => {
    try {
      const savedProfileRaw = localStorage.getItem(STORAGE_KEY)
      const savedExpiryRaw = localStorage.getItem(SESSION_EXPIRY_KEY)

      if (!savedProfileRaw || !savedExpiryRaw) {
        setLoggedInUser(null)
        return
      }

      const expiry = Number(savedExpiryRaw)
      const now = Date.now()

      if (!Number.isFinite(expiry) || now >= expiry) {
        localStorage.removeItem(STORAGE_KEY)
        localStorage.removeItem(SESSION_EXPIRY_KEY)
        setLoggedInUser(null)
        return
      }

      const savedProfile = JSON.parse(savedProfileRaw)
      const mergedProfile = {
        ...savedProfile,
        photo: savedProfile.photo || PROFILE_PHOTOS[savedProfile.username] || "",
      }

      setLoggedInUser(mergedProfile)
    } catch (err) {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(SESSION_EXPIRY_KEY)
      setLoggedInUser(null)
    }
  }, [])

  const displayName = useMemo(() => {
    if (!loggedInUser) return ""
    return loggedInUser.branchName || loggedInUser.username || "Broker"
  }, [loggedInUser])

  const handleSignOut = () => {
    try {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(SESSION_EXPIRY_KEY)
    } catch (err) {
      // ignore
    }

    setProfileOpen(false)
    setOpen(false)
    navigate("/login", { replace: true })
  }

  return (
    <header className={styles.crmTopNavWrap}>
      <div className={styles.crmTopNav}>
        <a
          className={styles.crmBrand}
          href="https://www.mortgagematrix.com.au"
          aria-label="Mortgage Matrix"
        >
          <img className={styles.crmLogo} src={logoPng} alt="Mortgage Matrix" />
        </a>

        <div className={styles.crmNavActions}>
          {loggedInUser && (
            <div className={styles.crmUserMenuWrap}>
              <button
                type="button"
                className={styles.crmUserMenuButton}
                onClick={() => setProfileOpen((v) => !v)}
                aria-expanded={profileOpen ? "true" : "false"}
                aria-label="Open user menu"
              >
                {loggedInUser.photo ? (
                  <img
                    src={loggedInUser.photo}
                    alt={displayName}
                    className={styles.crmUserAvatarImage}
                  />
                ) : (
                  <div className={styles.crmUserAvatarFallback}>
                    {getInitials(displayName)}
                  </div>
                )}

                <div className={styles.crmUserMeta}>
                  <span className={styles.crmUserName}>{displayName}</span>
                </div>

                <span className={styles.crmUserCaret} aria-hidden="true">
                  ▾
                </span>
              </button>

              {profileOpen && (
                <div className={styles.crmUserDropdown}>
                  <div className={styles.crmUserDropdownCard}>
                    {loggedInUser.photo ? (
                      <img
                        src={loggedInUser.photo}
                        alt={displayName}
                        className={styles.crmUserDropdownAvatar}
                      />
                    ) : (
                      <div className={styles.crmUserDropdownAvatarFallback}>
                        {getInitials(displayName)}
                      </div>
                    )}

                    <div className={styles.crmUserDropdownText}>
                      <div className={styles.crmUserDropdownName}>{displayName}</div>
                    </div>
                  </div>

                  <button
                    type="button"
                    className={styles.crmSignOutBtn}
                    onClick={handleSignOut}
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            className={styles.crmHamburger}
            aria-label="Open menu"
            aria-expanded={open ? "true" : "false"}
            onClick={() => setOpen((v) => !v)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      {open && (
        <div className={styles.crmMobileMenu} role="dialog" aria-label="Menu">
          {loggedInUser && (
            <div className={styles.crmMobileUserCard}>
              {loggedInUser.photo ? (
                <img
                  src={loggedInUser.photo}
                  alt={displayName}
                  className={styles.crmMobileUserAvatar}
                />
              ) : (
                <div className={styles.crmMobileUserAvatarFallback}>
                  {getInitials(displayName)}
                </div>
              )}

              <div className={styles.crmMobileUserText}>
                <div className={styles.crmMobileUserName}>{displayName}</div>
              </div>
            </div>
          )}

          {loggedInUser && (
            <button
              type="button"
              className={styles.crmMobileSignOutBtn}
              onClick={handleSignOut}
            >
              Sign Out
            </button>
          )}
        </div>
      )}
    </header>
  )
}